import { useEffect, useId, useState, ChangeEvent } from 'react';
import InputField from './InputField';
import Card from './Cards';
import { useTranslation } from 'react-i18next';

type WeightUnit = 'gr' | 'kg' | 'oz' | 'ozt' | 'lb';
type WeightValues = Record<WeightUnit, string | number>;

interface UnitHintProps {
  text: string;
}

function UnitHint({ text }: UnitHintProps) {
  const [open, setOpen] = useState(false);
  const tooltipId = useId();
  const { t } = useTranslation();

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-muted-foreground/50 text-[10px] leading-none text-muted-foreground"
        aria-label={t('weight.showUnitHint')}
        aria-expanded={open}
        aria-describedby={open ? tooltipId : undefined}
        onClick={() => setOpen((prev) => !prev)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        ?
      </button>
      {open && (
        <span
          id={tooltipId}
          role="tooltip"
          className="absolute left-0 top-full z-10 mt-1 w-64 rounded-md border border-border bg-popover px-2 py-1 text-[11px] leading-snug text-popover-foreground shadow-md"
        >
          {text}
        </span>
      )}
    </span>
  );
}

export default function Weight() {
  const { t } = useTranslation();

  useEffect(() => {
    document.title = t('weight.pageTitle');
  }, [t]);

  const coeffs: Record<WeightUnit, number> = {
    gr: 1,
    kg: 1 / 1000,
    oz: 0.0352739619,
    ozt: 0.0321507466,
    lb: 0.00220462262,
  };

  const [values, setValues] = useState<WeightValues>({
    gr: 100, kg: 0.1, oz: 3.52739619, ozt: 3.21507466, lb: 0.220462262,
  });

  function handleOnChange(event: ChangeEvent<HTMLInputElement>) {
    const name = event.target.name as WeightUnit;
    const value = event.target.value;
    const gr = Number(value) / coeffs[name];
    setValues({
      gr: name === 'gr' ? value : gr.toFixed(1),
      kg: name === 'kg' ? value : (gr * coeffs.kg).toFixed(3),
      oz: name === 'oz' ? value : (gr * coeffs.oz).toFixed(3),
      ozt: name === 'ozt' ? value : (gr * coeffs.ozt).toFixed(3),
      lb: name === 'lb' ? value : (gr * coeffs.lb).toFixed(3),
    });
  }

  const ozLabel = (
    <span className="inline-flex items-center gap-1">
      {t('weight.ounces')}
      <UnitHint text={t('weight.ouncesHint')} />
    </span>
  );

  const oztLabel = (
    <span className="inline-flex items-center gap-1">
      {t('weight.troyOunces')}
      <UnitHint text={t('weight.troyOuncesHint')} />
    </span>
  );

  return (
    <div className="max-w-sm">
      <Card title={t('weight.cardTitle')}>
        <InputField name="gr" label={t('weight.grams')} step="0.1" value={values.gr} unit="g" onChange={handleOnChange} />
        <InputField name="kg" label={t('weight.kilograms')} step="0.1" value={values.kg} unit="kg" onChange={handleOnChange} />
        <InputField name="oz" label={ozLabel} step="0.1" value={values.oz} unit="oz" onChange={handleOnChange} />
        <InputField name="ozt" label={oztLabel} step="0.1" value={values.ozt} unit="ozt" onChange={handleOnChange} />
        <InputField name="lb" label={t('weight.pounds')} step="0.1" value={values.lb} unit="lb" onChange={handleOnChange} />
      </Card>
    </div>
  );
}
